const SESSION_AUTH_KIND_KEY = 'keilog-sso.session-auth-kind';
const SESSION_AUTH_KIND = Object.freeze({
	LOCAL: 'local',
	SSO: 'sso',
});
const LOCAL_AUTH_KEYS = [
	'token',
	'expiry_at',
	'session_expired',
	SESSION_AUTH_KIND_KEY,
];

const SECURE_AUTH_KEYS = [
	'name',
	'email',
	'screen_permissions',
	'role',
	'franchise',
	'selectedFranchise',
];
const CREDENTIAL_METADATA_KEY = /(?:token|password|secret|credential|authorization)/i;
const MIN_SUCCESSOR_WAIT_MS = 250;
const MAX_SUCCESSOR_WAIT_MS = 300000;

function assertSafeSsoMetadata(value) {
	if (Array.isArray(value)) {
		value.forEach(assertSafeSsoMetadata);
		return;
	}
	if (!value || typeof value !== 'object') return;
	Object.entries(value).forEach(([key, child]) => {
		if (CREDENTIAL_METADATA_KEY.test(key)) {
			throw new Error('Credential-like SSO metadata is not allowed in browser storage.');
		}
		assertSafeSsoMetadata(child);
	});
}

function clearAuthenticationStorage(localStorageObject, secureStorage) {
	LOCAL_AUTH_KEYS.forEach(key => localStorageObject.removeItem(key));
	SECURE_AUTH_KEYS.forEach(key => secureStorage.remove(key));
}

function currentLocalToken(storage = window.localStorage) {
	const token = storage.getItem('token');
	return typeof token === 'string' && token.trim() !== '' ? token : null;
}

function currentSessionAuthKind(storage = window.localStorage) {
	return storage.getItem(SESSION_AUTH_KIND_KEY) === SESSION_AUTH_KIND.SSO
		? SESSION_AUTH_KIND.SSO
		: SESSION_AUTH_KIND.LOCAL;
}

async function waitForLocalToken({
	storage = window.localStorage,
	timeoutMs,
	windowObject = window,
	setTimer = setTimeout,
	clearTimer = clearTimeout,
} = {}) {
	if (
		!Number.isInteger(timeoutMs)
		|| timeoutMs < MIN_SUCCESSOR_WAIT_MS
		|| timeoutMs > MAX_SUCCESSOR_WAIT_MS
	) {
		throw new Error('Invalid local successor wait configuration.');
	}
	if (
		!windowObject
		|| typeof windowObject.addEventListener !== 'function'
		|| typeof windowObject.removeEventListener !== 'function'
	) {
		throw new Error('Local successor storage events are unavailable.');
	}

	const existing = currentLocalToken(storage);
	if (existing !== null) return existing;

	return new Promise(resolve => {
		let settled = false;
		let timer;
		const finish = (token) => {
			if (settled) return;
			settled = true;
			windowObject.removeEventListener('storage', onStorage);
			if (timer !== undefined) clearTimer(timer);
			resolve(token);
		};
		const onStorage = (event) => {
			if (
				event.key !== 'token'
				|| (event.storageArea && event.storageArea !== storage)
			) return;
			const token = currentLocalToken(storage);
			if (token !== null) finish(token);
		};

		windowObject.addEventListener('storage', onStorage);
		timer = setTimer(() => finish(currentLocalToken(storage)), timeoutMs);
		// Close the read/listen gap if the winning tab committed just before registration.
		const successor = currentLocalToken(storage);
		if (successor !== null) finish(successor);
	});
}

function sessionExpiryMillis(result, now = Date.now()) {
	const isSso = !!result && !!result.sso && result.sso.auth_kind === SESSION_AUTH_KIND.SSO;
	const value = isSso
		? result.sso.session_expires_at
		: result && result.expires_at;

	if (typeof value === 'number' && Number.isFinite(value)) {
		return value > 100000000000 ? value : value * 1000;
	}
	if (typeof value === 'string' && value.trim() !== '') {
		const numeric = Number(value);
		if (Number.isFinite(numeric)) {
			return numeric > 100000000000 ? numeric : numeric * 1000;
		}
		const parsed = Date.parse(value);
		if (Number.isFinite(parsed)) {
			return parsed;
		}
	}
	if (isSso) throw new Error('Invalid SSO session expiry.');

	const fallback = Number(process.env.VUE_APP_LEGACY_SESSION_FALLBACK_MS);
	if (!Number.isInteger(fallback) || fallback <= 0) {
		throw new Error('Invalid legacy session fallback configuration.');
	}
	return now + fallback;
}

function sessionAuthKind(result) {
	if (!result || result.sso === undefined || result.sso === null) {
		return SESSION_AUTH_KIND.LOCAL;
	}
	const authKind = result.sso.auth_kind;
	if (authKind !== SESSION_AUTH_KIND.LOCAL && authKind !== SESSION_AUTH_KIND.SSO) {
		throw new Error('Invalid KEiLOG session kind.');
	}
	if (
		(authKind === SESSION_AUTH_KIND.SSO && (
			result.sso.session_expires_at === null
			|| result.sso.session_expires_at === undefined
		))
		|| (authKind === SESSION_AUTH_KIND.LOCAL && result.sso.session_expires_at !== null)
	) {
		throw new Error('Invalid KEiLOG session binding metadata.');
	}
	return authKind;
}

function persistLocalLoginResult(result, storage = window.localStorage) {
	if (!result || typeof result !== 'object' || typeof result.access_token !== 'string' || result.access_token.trim() === '') {
		throw new Error('Invalid KEiLOG login response.');
	}
	assertSafeSsoMetadata(result.sso);
	const authKind = sessionAuthKind(result);
	const expiryAt = sessionExpiryMillis(result);

	storage.removeItem('session_expired');
	storage.setItem('expiry_at', String(expiryAt));
	storage.setItem(SESSION_AUTH_KIND_KEY, authKind);
	// Publish the bearer last so same-origin successor tabs never observe a new token with old metadata.
	storage.setItem('token', result.access_token);
}

function isSameLocalSession(expectedToken, storage = window.localStorage) {
	return expectedToken !== null && currentLocalToken(storage) === expectedToken;
}

function persistExactSessionMetadata(expectedToken, metadata, storage = window.localStorage) {
	if (!isSameLocalSession(expectedToken, storage)) return false;
	if (!metadata || typeof metadata !== 'object' || metadata.active !== true) {
		throw new Error('Invalid KEiLOG session status.');
	}
	assertSafeSsoMetadata(metadata);
	const authKind = sessionAuthKind({ sso: metadata });
	const expiryAt = authKind === SESSION_AUTH_KIND.SSO
		? sessionExpiryMillis({ sso: metadata })
		: null;
	if (!isSameLocalSession(expectedToken, storage)) return false;
	if (expiryAt !== null) storage.setItem('expiry_at', String(expiryAt));
	storage.setItem(SESSION_AUTH_KIND_KEY, authKind);
	return true;
}

function clearExactAuthenticationStorage(expectedToken, storage, secureStorage) {
	if (!isSameLocalSession(expectedToken, storage)) return false;
	clearAuthenticationStorage(storage, secureStorage);
	return true;
}

function finishExactExpiredSession({
	expectedToken,
	storage,
	secureStorage,
	publishAuthenticationChanged,
}) {
	if (typeof publishAuthenticationChanged !== 'function') {
		throw new Error('Expired session generation publisher is unavailable.');
	}
	if (!clearExactAuthenticationStorage(expectedToken, storage, secureStorage)) return false;
	publishAuthenticationChanged();
	storage.setItem('session_expired', 'true');
	return true;
}

function isSignedOutSsoResult(result) {
	return !!result
		&& typeof result === 'object'
		&& !Object.prototype.hasOwnProperty.call(result, 'access_token')
		&& !!result.sso
		&& typeof result.sso === 'object'
		&& result.sso.auth_kind === 'signed_out'
		&& result.sso.session_expires_at === null
		&& typeof result.sso.return_path === 'string';
}

function clearExactSignedOutSession(
	result,
	expectedToken,
	storage,
	secureStorage,
) {
	if (!isSignedOutSsoResult(result)) {
		return false;
	}
	return clearExactAuthenticationStorage(expectedToken, storage, secureStorage);
}

function finishExactSignedOutSession({
	result,
	expectedToken,
	storage,
	secureStorage,
	destination,
	resetRuntimeAuth,
	beforeNavigate,
	hardNavigate,
}) {
	if (
		typeof destination !== 'string'
		|| typeof resetRuntimeAuth !== 'function'
		|| typeof beforeNavigate !== 'function'
		|| typeof hardNavigate !== 'function'
	) {
		throw new Error('Signed-out transition is unavailable.');
	}
	if (!clearExactSignedOutSession(result, expectedToken, storage, secureStorage)) {
		return false;
	}

	resetRuntimeAuth();
	beforeNavigate();
	hardNavigate(destination);

	return true;
}

module.exports = {
	SESSION_AUTH_KIND,
	clearExactAuthenticationStorage,
	clearExactSignedOutSession,
	clearAuthenticationStorage,
	currentLocalToken,
	currentSessionAuthKind,
	finishExactExpiredSession,
	finishExactSignedOutSession,
	isSignedOutSsoResult,
	isSameLocalSession,
	persistExactSessionMetadata,
	persistLocalLoginResult,
	sessionExpiryMillis,
	waitForLocalToken,
};
